import React from 'react';
import { Droplet, Info, Calculator, Utensils, Coffee, AlertTriangle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета нормы потребления воды
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Определяем процент воды из чистой воды, еды и других напитков
  const pureWaterPercent = Math.round((results.recommendedWaterIntakeMl / results.recommendedTotalIntakeMl) * 100);
  const foodWaterPercent = Math.round((results.waterFromFoodMl / results.recommendedTotalIntakeMl) * 100);
  const otherBeveragesPercent = Math.round((results.waterFromOtherBeveragesMl / results.recommendedTotalIntakeMl) * 100);

  // Получаем уровень водопотребления для сравнения
  const getWaterIntakeLevel = (ml) => {
    if (ml < 1500) return { level: 'Низкий', color: 'red' };
    if (ml < 2000) return { level: 'Ниже рекомендуемого', color: 'amber' };
    if (ml < 3000) return { level: 'Оптимальный', color: 'green' };
    return { level: 'Повышенный', color: 'blue' };
  };

  const intakeLevel = getWaterIntakeLevel(results.recommendedTotalIntakeMl);

  return (
    <ToolResult title="Результаты расчета нормы воды">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Чистая вода */}
        <div className="water-wave rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm" style={{'--wave-height': `${pureWaterPercent}%`}}>
          <div className="water-wave-content">
            <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
              <Droplet className="w-4 h-4 water-drop"/>
              Рекомендуемая норма воды
            </h3>
            <div className="flex items-baseline flex-wrap gap-x-2">
              <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
                {formatNumber(results.recommendedWaterIntakeMl, 0)} мл
              </span>
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                чистой воды в день
              </span>
            </div>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              Это примерно {results.numberOfContainers} {pluralizeContainer(results.numberOfContainers)} по {results.containerSizeMl} мл
            </p>
          </div>
        </div>

        {/* Общая норма жидкости */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Info className="w-4 h-4"/>
            Общая норма жидкости
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatNumber(results.recommendedTotalIntakeMl, 0)} мл
            </span>
            <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
              ({intakeLevel.level})
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Включает воду из пищи и других напитков
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Чистая питьевая вода" 
          value={`${formatNumber(results.recommendedWaterIntakeMl, 0)} мл (${pureWaterPercent}%)`} 
          color="blue" 
          icon={<Calculator className="w-4 h-4"/>}
        />
        
        {results.waterFromFoodMl > 0 && (
          <ResultItem 
            label="Вода из пищи" 
            value={`${formatNumber(results.waterFromFoodMl, 0)} мл (${foodWaterPercent}%)`} 
            color="green" 
            icon={<Utensils className="w-4 h-4"/>}
          />
        )}
        
        {results.waterFromOtherBeveragesMl > 0 && (
          <ResultItem 
            label="Другие напитки" 
            value={`${formatNumber(results.waterFromOtherBeveragesMl, 0)} мл (${otherBeveragesPercent}%)`} 
            color="amber" 
            icon={<Coffee className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Оптимальное количество емкостей" 
          value={`${results.numberOfContainers} × ${results.containerSizeMl} мл`}
          color="blue"
        />
        
        <ResultItem 
          label="Рекомендуемый объем на 1 кг веса" 
          value={`${results.baseFactors.baseWaterPerKg} мл/кг`} 
        />
        
        <ResultItem 
          label="Учтенные факторы" 
          value={`Активность: ×${formatNumber(results.adjustmentFactors.activity, 2)}, Климат: ×${formatNumber(results.adjustmentFactors.climate, 2)}, Сезон: ×${formatNumber(results.adjustmentFactors.season, 2)}`} 
        />
      </div>

      {/* Рекомендации */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-5 mt-6 border border-blue-200 dark:border-blue-700">
        <h4 className="text-lg font-medium text-blue-800 dark:text-blue-300 mb-3">
          Персональные рекомендации
        </h4>
        <ul className="space-y-2">
          {results.recommendations.map((recommendation, index) => (
            <li key={index} className="flex items-start gap-2 text-blue-700 dark:text-blue-400">
              <Droplet className="w-4 h-4 mt-1 flex-shrink-0" />
              <span className="text-sm">{recommendation}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Предупреждения, если они есть */}
      {results.healthWarnings && results.healthWarnings.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 mt-4 border border-yellow-200 dark:border-yellow-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-1">Важное предупреждение о здоровье</h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                Указанные вами состояния здоровья ({results.healthWarnings.join(", ")}) требуют 
                консультации с врачом для определения оптимальной нормы потребления воды. 
                Приведенный расчет является приблизительным.
              </p>
            </div>
          </div>
        </div>
      )}
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "емкость" в зависимости от числа
const pluralizeContainer = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'емкостей';
  if (lastDigit === 1) return 'емкость';
  if (lastDigit >= 2 && lastDigit <= 4) return 'емкости';
  return 'емкостей';
};

export default ResultsOverview;